import axios from 'axios';
import config from '../config';
import {
  ArticleRemapDataResponse,
  ArticleRemapExportRow,
} from '../types/articleRemap';

export class ArticleRemapServiceError extends Error {
  messages: string[];

  constructor(messages: string[]) {
    super(messages[0] || 'Kunde inte skapa remap-filen.');
    this.name = 'ArticleRemapServiceError';
    this.messages = messages;
  }
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : undefined;
};

const parseFilenameFromDisposition = (
  contentDisposition: string | undefined,
): string | null => {
  if (!contentDisposition) return null;

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ''));
  }

  const plainMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  return plainMatch?.[1] ?? null;
};

const extractMessages = (data: unknown): string[] => {
  const responseData = data as
    | {
        error?: string;
        errors?: Array<{ message?: string } | string>;
      }
    | undefined;

  if (!responseData) return [];

  if (Array.isArray(responseData.errors) && responseData.errors.length > 0) {
    return responseData.errors
      .map((item) => (typeof item === 'string' ? item : item.message))
      .filter((message): message is string => Boolean(message));
  }

  return responseData.error ? [responseData.error] : [];
};

export async function fetchArticleRemapData(
  productId: number,
): Promise<ArticleRemapDataResponse> {
  const response = await axios.get<ArticleRemapDataResponse>(
    `${config.API_URL}/products/${productId}/remap-data`,
    { headers: getAuthHeaders() },
  );

  return response.data;
}

export async function downloadArticleRemapWorkbook(
  productId: number,
  rows: ArticleRemapExportRow[],
): Promise<{ blob: Blob; filename: string | null }> {
  try {
    const response = await axios.post(
      `${config.API_URL}/products/${productId}/article-remap/export`,
      { rows },
      {
        headers: getAuthHeaders(),
        responseType: 'blob',
      },
    );

    return {
      blob: response.data,
      filename: parseFilenameFromDisposition(
        response.headers['content-disposition'],
      ),
    };
  } catch (error) {
    if (axios.isAxiosError(error) && error.response?.data instanceof Blob) {
      const text = await error.response.data.text();
      try {
        const parsed = JSON.parse(text);
        const messages = extractMessages(parsed);
        if (messages.length > 0) {
          throw new ArticleRemapServiceError(messages);
        }
      } catch (parseError) {
        if (parseError instanceof ArticleRemapServiceError) {
          throw parseError;
        }
      }
    }

    throw error;
  }
}
